import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import { Tooltip } from '@mui/material';
import { TimeMode } from '@types';
import { SiteMeta } from '@src/types/SiteMeta';
import { currentTzString } from '@utils';
import { COLUMN_FLEX, headerStyles } from '../styles';
import { formatTimestamp } from '../utils/incidentUtils';
import DeviceNameCell from '../components/Cells/DeviceNameCell';
import TemperatureCell from '../components/Cells/TemperatureCell';
import TimestampCell from '../components/Cells/TimestampCell';
import HistoricalChartCell from '../components/Cells/HistoricalChartCell';

interface UseIncidentColumnsReturn extends Array<GridColDef> {}

const COLUMN_HEADERS = {
  DEVICE_NAME: 'Device Name',
  UNIT_ID: 'Unit ID',
  RACK_ID: 'Rack ID',
  MAX_CELL_T: 'Max Cell Temperature',
  INCIDENT_TIME: 'Incident Time',
  MAX_CELL_T_HISTORY: 'Max Cell T History',
} as const;

const TOOLTIPS = {
  MAX_CELL_T: 'Temperature value recorded at the moment the incident occurred',
} as const;

export const useIncidentColumns = (
  timeMode?: TimeMode,
  siteMeta?: SiteMeta,
): UseIncidentColumnsReturn => {
  const theme = useTheme();

  return useMemo(
    () => [
      {
        field: 'deviceName',
        headerName: COLUMN_HEADERS.DEVICE_NAME,
        ...COLUMN_FLEX.DEVICE_NAME,
        sortable: true,
        filterable: true,
        renderCell: (params) => (
          <DeviceNameCell params={params} theme={theme} />
        ),
      },
      {
        field: 'unitId',
        headerName: COLUMN_HEADERS.UNIT_ID,
        ...COLUMN_FLEX.UNIT_ID,
        sortable: true,
        filterable: true,
      },
      {
        field: 'rackId',
        headerName: COLUMN_HEADERS.RACK_ID,
        ...COLUMN_FLEX.RACK_ID,
        sortable: true,
        filterable: true,
      },
      {
        field: 'maxCellT',
        headerName: 'Alert Max Cell T (°C)',
        ...COLUMN_FLEX.TEMPERATURE,
        type: 'number',
        sortable: true,
        filterable: true,
        align: 'center',
        headerAlign: 'center',
        renderHeader: () => (
          <Tooltip title={TOOLTIPS.MAX_CELL_T}>
            <p style={headerStyles}>{COLUMN_HEADERS.MAX_CELL_T}</p>
          </Tooltip>
        ),
        renderCell: (params) => (
          <TemperatureCell params={params} theme={theme} />
        ),
      },
      {
        field: 'timestamp',
        headerName: COLUMN_HEADERS.INCIDENT_TIME,
        ...COLUMN_FLEX.TIMESTAMP,
        sortable: true,
        filterable: false,
        renderHeader: () => {
          const timezoneString =
            timeMode && siteMeta
              ? currentTzString({
                  timeMode,
                  lat: siteMeta.GPSLat,
                  long: siteMeta.GPSLong,
                })
              : 'UTC±00:00';

          return (
            <Tooltip title={`Timezone: ${timezoneString}`}>
              <p style={headerStyles}>{COLUMN_HEADERS.INCIDENT_TIME}</p>
            </Tooltip>
          );
        },
        renderCell: (params) => (
          <TimestampCell
            params={params}
            formatTimestamp={(timestamp: string) =>
              formatTimestamp(timestamp, timeMode, siteMeta)
            }
          />
        ),
      },
      {
        field: 'historicalData',
        headerName: COLUMN_HEADERS.MAX_CELL_T_HISTORY,
        ...COLUMN_FLEX.HISTORICAL_CHART,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => <HistoricalChartCell params={params} />,
      },
    ],
    [theme, timeMode, siteMeta],
  );
};
